import { PlaceBetPayload, BetPlacedResponse, SessionProfitLoss } from '@/types/Bet';
import { apiSlice } from './apiSlice';
import toast from 'react-hot-toast';

export interface BetHistoryQueryParams {
  page?: number;
  limit?: number;
  betTime: 'CURRENT' | 'SETTLED' | 'PAST';
  fromDate?: string; // ISO date-time
  toDate?: string; // ISO date-time
  eventId?: number;
  sport?: string;
  search?: string;
  betType?: string;
}
export interface MarketExposureResponse {
  marketId: string | null;
  marketExternalId: string;
  selections: SelectionExposure[];
}

export interface SelectionExposure {
  selectionId: string;
  exposure: number;
}

export interface SessionBookset {
  selection_id: string;
  max_profit: number;
  max_loss: number;
  profit_loss: {
    position: number;
    profit_or_loss: number;
  }[];
}

export interface StakeOption {
  label: string;
  value: number;
}

export interface StakeSetResponse {
  success: boolean;
  message: string;
  stakeSet: StakeOption[];
}

export interface UpdateStakeSetPayload {
  data: StakeOption[];
}

export interface UpdateStakeSetResponse {
  success: boolean;
  message: string;
}

export interface OpenBetSummaryItem {
  eventId: string;
  eventExternalId: string;
  eventName: string;
  marketId: string;
  marketName: string;
  marketType: string;
  betCount: number;
}

export interface OpenBetsSummaryResponse {
  success: boolean;
  message: string;
  bets: OpenBetSummaryItem[];
}

export interface BetHistoryResponse {
  success: boolean;
  message: string;
  bets: BetHistory[];
  pagination: Pagination;
}

// Individual Bet
export interface BetHistory {
  id: string;
  userId: string;
  eventId: string;
  sport: string;
  marketId: string;
  marketName: string;
  marketCategory: string | null;
  marketType: MarketType;
  amount: string;
  odds: string;
  percentage: number | null;
  betOn: BetOnType;
  selection: string;
  selectionId: string;
  payout: string;
  status: BetStatus;
  isBookmaker: boolean;
  placedAt: string; // ISO Date string
  settledAt: string | null; // ISO Date string or null
  ip: string;
  isTurnOverCalculated: boolean;
  isLossBackCalc: boolean;
  isReferralLossCommissionCalc: boolean;
  event: EventInfo;
  liability: number;
  potentialProfit: number;
}

// Event Info inside Bet
export interface EventInfo {
  id: string;
  name: string;
  externalId: string;
}

// Pagination Info
export interface Pagination {
  currentPage: number;
  limit: number;
  totalItems: number;
  totalPage: number;
}

// Profit Loss API

export interface BetProfitLossResponse extends ProfitLossResponse {
  filteredProfitLoss?: string | number;
  totalProfitLossAllSports?: string | number;
}
export interface ProfitLossResponse {
  success?: boolean;
  message?: string;
  bets?: ProfitLossItem[];
  pagination?: Pagination;
}
export interface ProfitLossItem {
  id?: string;
  eventId?: string;
  marketId?: string;
  marketName?: string;
  marketType?: string;
  selectionId?: string;
  selectionName?: string;
  runnerName?: string;
  rate?: string;
  stake?: string;
  pl?: string; // Profit/Loss
  betOn?: 'BACK' | 'LAY';
  createdAt?: string;
  eventName?: string;
  matchId?: string;
  amount?: string | number;
  odds?: string | number;
  selection?: string;
  [key: string]: any;
}

export interface ProfitLossQueryParams {
  page?: number;
  limit?: number;
  sport: string;
  fromDate?: string;
  toDate?: string;
  search?: string;
}

// Enums for better type safety
export type BetOnType = 'Back' | 'Lay';

export type BetStatus = 'Pending' | 'Won' | 'Lost' | 'Cancelled' | 'Void';

export type MarketType = 'PREMIUM' | 'NORMAL' | 'FANCY' | 'BOOKMAKER';

export const betApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getBookset: builder.query<MarketExposureResponse[], string>({
      query: (eventId) => {
        return `/exposure/bookset/calculation?eventId=${eventId}&t=${Date.now()}`;
      },
      providesTags: ['Bets'],
    }),
    betPlace: builder.mutation<BetPlacedResponse, PlaceBetPayload>({
      query: (credentials) => ({
        url: '/bet/place',
        method: 'POST',
        body: credentials,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data.message || 'Bet placed successfully!', { id: 'bet-place-success' });
        } catch (error: any) {
          console.log('error', error);
          toast.error(error?.error?.data?.message || 'Failed to place bet', {
            id: 'bet-place-error',
          });
        }
      },
      invalidatesTags: ['Bets'],
    }),
    getStakes: builder.query<StakeSetResponse, void>({
      query: () => ({
        url: '/stakeset',
        params: {
          t: Date.now(),
        },
      }),
      providesTags: ['Bets'],
    }),
    updateStakes: builder.mutation<UpdateStakeSetResponse, UpdateStakeSetPayload>({
      query: (body) => ({
        url: '/stakeset',
        method: 'PUT',
        body,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data.message || 'Stakes value updated successfully!', {
            id: 'update-stake-success',
          });
        } catch (error: any) {
          console.log('error', error);
          toast.error(error?.error?.data?.message || 'Failed to update stakes', {
            id: 'update-stake-error',
          });
        }
      },
      invalidatesTags: ['Bets'],
    }),
    getOpenBetsSummary: builder.query<OpenBetsSummaryResponse, void>({
      query: () => ({
        url: '/bet/open-bets/summary',
        method: 'GET',
        params: { t: Date.now() },
      }),
      providesTags: ['Bets'],
    }),
    getSessionPL: builder.query<
      SessionProfitLoss[],
      { eventId: string; selectionId: string; marketId: string }
    >({
      query: ({ eventId, selectionId, marketId }) => ({
        url: '/exposure/profit-loss',
        params: {
          eventId,
          selectionId,
          marketId,
          t: Date.now(),
        },
      }),
      providesTags: ['Bets'],
    }),
    getBetHistory: builder.query<BetHistoryResponse, BetHistoryQueryParams>({
      query: (params) => ({
        url: '/bet/history',
        params: {
          page: params.page,
          limit: params.limit,
          betTime: params.betTime,
          fromDate: params.fromDate,
          toDate: params.toDate,
          eventId: params.eventId,
          sport: params.sport,
          search: params.search,
          betType: params.betType,
          t: Date.now(),
        },
      }),
      providesTags: ['Bets'],
    }),
    getProfitLoss: builder.query<BetProfitLossResponse, ProfitLossQueryParams>({
      query: (params) => ({
        url: '/bet/profit-loss',
        method: 'GET',
        params: { ...params, t: Date.now() },
      }),
    }),
  }),
});

export const {
  useBetPlaceMutation,
  useGetBetHistoryQuery,
  useGetBooksetQuery,
  useGetStakesQuery,
  useGetSessionPLQuery,
  useUpdateStakesMutation,
  useGetOpenBetsSummaryQuery,
  useGetProfitLossQuery,
} = betApi;
